/**
 * Validation hook for SmartTimeInput
 * Handles validation logic during blur events, separating concerns from the main combobox hook
 */

import { useCallback, useState } from 'react'
import type { Temporal } from '@js-temporal/polyfill'

import type { ValidationError } from '../types'

export interface UseValidationOptions {
  parseTime: (input: string) => {
    success: boolean
    time: Temporal.PlainTime | null
    error?: string
  }
  isValidInput: (input: string) => boolean
  required: boolean
  onValidationError?: (error: ValidationError) => void
}

export interface UseValidationReturn {
  validationError: ValidationError | null
  validateAndCommitOnBlur: (
    inputValue: string,
    commitValue: (time: Temporal.PlainTime | null) => void,
    createRangeError: (originalInput: string) => ValidationError,
  ) => void
  setValidationError: (error: ValidationError | null) => void
}

export function useValidation({
  parseTime,
  isValidInput,
  required,
  onValidationError,
}: UseValidationOptions): UseValidationReturn {
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null)

  const validateAndCommitOnBlur = useCallback(
    (
      inputValue: string,
      commitValue: (time: Temporal.PlainTime | null) => void,
      createRangeError: (originalInput: string) => ValidationError,
    ) => {
      if (inputValue.trim()) {
        const result = parseTime(inputValue)
        if (result.success && result.time && isValidInput(inputValue)) {
          // commitValue will clear the validation error via useValueCommit
          commitValue(result.time)
        } else if (result.error === 'Time is outside allowed range') {
          const error = createRangeError(inputValue)
          setValidationError(error)
          onValidationError?.(error)
        } else if (required) {
          const error: ValidationError = {
            type: 'invalid',
            message: 'Please enter a valid time',
            originalInput: inputValue,
          }
          setValidationError(error)
          onValidationError?.(error)
        }
      } else if (required) {
        const error: ValidationError = {
          type: 'required',
          message: 'Time is required',
          originalInput: inputValue,
        }
        setValidationError(error)
        onValidationError?.(error)
      } else {
        // commitValue will clear the validation error via useValueCommit
        commitValue(null)
      }
    },
    [parseTime, isValidInput, required, onValidationError],
  )

  return {
    validationError,
    validateAndCommitOnBlur,
    setValidationError,
  }
}
